import { useState } from "react";
import { cn } from "tailwind-variants";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import BackgroundToggle from "./common/BackgroundToggle.vue";
import { useSpanglesStore } from "./spangles-module";
import { useDtfStore } from "./store";
import { hasGangSheetError, shouldUseEnhancedMetadata } from "./utils";
import "./ArtworkInfo.scss";

export default function ArtworkInfo({
  artwork = null,
  index = 0,
  showFileInfo = true,
  isMultiUpload = false,
  ...restProps
}) {
  const dtfStore = useDtfStore();
  const spanglesStore = useSpanglesStore();

  const [backgroundTypeModel, setBackgroundTypeModel] = useState(dtfStore.backgroundType);

  function showModal() {
    return dtfStore.showModal;
  }

  function sizeErrorTypes() {
    return dtfStore.sizeErrorTypes;
  }

  function isSpangle() {
    return dtfStore.isSpangle;
  }

  function showStepsUI() {
    return dtfStore.showStepsUI;
  }

  function showRedrawUI() {
    return dtfStore.showRedrawUI;
  }

  function isStandardArtwork() {
    return dtfStore.isStandardArtwork;
  }

  function backgroundType() {
    return dtfStore.backgroundType;
  }

  function experiments() {
    return dtfStore.experiments;
  }

  function isStandardDTFProduct() {
    return dtfStore.isStandardDTFProduct;
  }

  function allAPIsCompleted() {
    return spanglesStore.allAPIsCompleted;
  }

  function metadata() {
    const { removeBackground, metadata, withBgMetadata } = artwork || {}; // eslint-disable-line no-shadow
    if (!withBgMetadata || Object.keys(withBgMetadata).length === 0) {
      return metadata;
    }
    return removeBackground ? metadata : withBgMetadata;
  }

  function withError() {
    return (
      metadata()?.errors?.length > 0 ||
      artwork?.errorMessage ||
      artwork?.uploadError ||
      artwork?.retryFailed
    );
  }

  function hasGangSheetErrorComputed() {
    return hasGangSheetError(artwork);
  }

  function showRemoveButton() {
    const hasSizeError = sizeErrorTypes().includes(artwork?.errorType?.toString());
    const shouldShowButton = hasSizeError || showModal() || !withError();
    if (isStandardArtwork()) {
      return shouldShowButton && artwork?.enhancementCompleted;
    }
    if (isSpangle()) {
      return shouldShowButton && allAPIsCompleted();
    }
    return shouldShowButton;
  }

  function svgIcon() {
    const icon = showSteps() ? "empty-image-circle" : "check-bold";
    return withError() ? "cross" : icon;
  }

  function showArtworkInfo() {
    return (
      showModal() ||
      ((metadata() || artwork?.isLoading) && !artwork?.uploadError && !artwork?.retryFailed)
    );
  }

  function largeBottomMargin() {
    return showSteps() && !artwork?.isLoading;
  }

  function trimmedText() {
    return metadata()?.trimmed ? "Trimmed size" : "Original size";
  }

  function enhancedWidthInch() {
    if (isStandardArtwork()) {
      const useEnhanced = shouldUseEnhancedMetadata(artwork);
      if (!useEnhanced) {
        return artwork?.originalMetadata?.widthInch || metadata()?.widthInch;
      }
      return artwork?.removeBackground
        ? artwork?.enhancedMetadata?.widthInch || metadata()?.widthInch
        : artwork?.withBgMetadata?.widthInch || metadata()?.widthInch;
    }
    return metadata()?.widthInch;
  }

  function enhancedHeightInch() {
    if (isStandardArtwork()) {
      const useEnhanced = shouldUseEnhancedMetadata(artwork);
      if (!useEnhanced) {
        return artwork?.originalMetadata?.heightInch || metadata()?.heightInch;
      }
      return artwork?.removeBackground
        ? artwork?.enhancedMetadata?.heightInch || metadata()?.heightInch
        : artwork?.withBgMetadata?.heightInch || metadata()?.heightInch;
    }
    return metadata()?.heightInch;
  }

  function enhancedWidthPixel() {
    if (isStandardArtwork()) {
      const useEnhanced = shouldUseEnhancedMetadata(artwork);
      if (!useEnhanced) {
        return artwork?.originalMetadata?.width || metadata()?.width;
      }
      return artwork?.removeBackground
        ? artwork?.enhancedMetadata?.width || metadata()?.width
        : artwork?.withBgMetadata?.width || metadata()?.width;
    }
    return metadata()?.width;
  }

  function enhancedHeightPixel() {
    if (isStandardArtwork()) {
      const useEnhanced = shouldUseEnhancedMetadata(artwork);
      if (!useEnhanced) {
        return artwork?.originalMetadata?.height || metadata()?.height;
      }
      return artwork?.removeBackground
        ? artwork?.enhancedMetadata?.height || metadata()?.height
        : artwork?.withBgMetadata?.height || metadata()?.height;
    }
    return metadata()?.height;
  }

  function dimensionText() {
    return `${enhancedWidthInch()}" x ${enhancedHeightInch()}"`;
  }

  function pixelDimensionText() {
    return `(${enhancedWidthPixel()}px x ${enhancedHeightPixel()}px)`;
  }

  function showSteps() {
    return showStepsUI() && !isMultiUpload;
  }

  function setRemovalFile(...args) {
    return dtfStore.setRemovalFile(...args);
  }

  function setBackgroundType(...args) {
    return dtfStore.setBackgroundType(...args);
  }

  function handleBackgroundTypeModelChange(value) {
    setBackgroundTypeModel(value);
    setBackgroundType(value);
  }

  function onRemove() {
    setRemovalFile(artwork?.id);
  }

  return (
    <>
      {showArtworkInfo() ? (
        <div
          {...restProps}
          className={cn("artwork-info scope-ab916ad0", restProps?.className, {
            "artwork-info--large-bottom-margin":
              largeBottomMargin() && !(isMultiUpload && showRedrawUI()),
            "artwork-info--standard-artwork": isStandardArtwork(),
            "artwork-info--no-margin": artwork?.isLoading
          })}
        >
          {metadata() && !artwork?.isLoading ? (
            <>
              <div
                className={cn("artwork-info__file-info-row scope-ab916ad0", {
                  "artwork-info__file-info-row--with-error": withError()
                })}
              >
                {showFileInfo ? (
                  <>
                    <UseSvg
                      className={cn("artwork-info__file-info-icon scope-ab916ad0", {
                        "artwork-info__file-info-icon--large": showSteps()
                      })}
                      icon={svgIcon()}
                      data-testid="artwork-info-file-info-icon"
                    />
                    {showSteps() ? (
                      <div className="scope-ab916ad0">
                        {artwork?.name} <br className="scope-ab916ad0" />
                        <b className="scope-ab916ad0">
                          {trimmedText()} {dimensionText()}, {pixelDimensionText()}
                        </b>
                      </div>
                    ) : (
                      <div className="scope-ab916ad0">
                        <b className="scope-ab916ad0">
                          {trimmedText()} {dimensionText()}
                        </b>
                        <br className="scope-ab916ad0" /> {pixelDimensionText()}
                      </div>
                    )}
                  </>
                ) : null}
              </div>
              {!isStandardDTFProduct() ? (
                <div className="artwork-info__actions scope-ab916ad0">
                  {!hasGangSheetErrorComputed() ? (
                    <BackgroundToggle
                      className="scope-ab916ad0"
                      modelValue={backgroundTypeModel}
                      onUpdateModelValue={handleBackgroundTypeModelChange}
                      data-testid="background-toggle"
                    />
                  ) : null}
                  {showRemoveButton() ? (
                    <button
                      className="artwork-info__remove-button scope-ab916ad0"
                      type="button"
                      onClick={onRemove}
                      data-testid="artwork-info-remove-button"
                    >
                      <UseSvg
                        className="artwork-info__trash-icon scope-ab916ad0"
                        icon="trash"
                        data-testid="artwork-info-trash-icon"
                      />{" "}
                      Remove{" "}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
